import { JsonSerializable } from '@/util/object/JsonSerializable'

export const UNKNOWN_TYPE = 'UNKNOWN_TYPE'

export class EnumType extends JsonSerializable {
  #value
  #allowedValues = []

  constructor (jsonObject, allowedValues) {
    super()
    this.#allowedValues = allowedValues
    this.#value = this.#allowedValues.find(allowedValue => allowedValue === jsonObject) || UNKNOWN_TYPE
  }

  /**
   * EnumType value
   * @return {string}
   */
  get value () {
    return this.#value
  }

  /**
   * Checks if this Enumerated type is
   * equal to provided type
   * @param {string} type
   * @return {boolean}
   */
  is (type) {
    return this.#value === type
  }

  /**
   * @return {Object}
   */
  toJSON () {
    return this.#value
  }
}

export class FulfillmentType extends EnumType {
  static DIGITAL = 'DIGITAL'
  static PHYSICAL = 'PHYSICAL'

  constructor (jsonObject) {
    super(jsonObject, [
      FulfillmentType.DIGITAL,
      FulfillmentType.PHYSICAL
    ])
    Object.freeze(this)
  }

  /**
   * @param {Object} jsonObject
   * @return {FulfillmentType|undefined}
   */
  static fromJSON (jsonObject) {
    return (jsonObject && new FulfillmentType(jsonObject)) || undefined
  }
}

export class RewardLinkStatus extends EnumType {
  static ACTIVE = 'ACTIVE'
  static EXPIRED = 'EXPIRED'
  static LOCKED = 'LOCKED'

  constructor (jsonObject) {
    super(jsonObject, [
      RewardLinkStatus.ACTIVE,
      RewardLinkStatus.EXPIRED,
      RewardLinkStatus.LOCKED
    ])
    Object.freeze(this)
  }

  /**
   * @param {Object} jsonObject
   * @return {RewardLinkStatus|undefined}
   */
  static fromJSON (jsonObject) {
    return (jsonObject && new RewardLinkStatus(jsonObject)) || undefined
  }
}

export class FulfillmentStatus extends EnumType {
  static PENDING = 'PENDING'
  static COMPLETE = 'COMPLETE'
  static FAILED = 'FAILED'

  constructor (jsonObject) {
    super(jsonObject, [
      FulfillmentStatus.PENDING,
      FulfillmentStatus.COMPLETE,
      FulfillmentStatus.FAILED
    ])
    Object.freeze(this)
  }

  /**
   * @param {Object} jsonObject
   * @return {FulfillmentStatus|undefined}
   */
  static fromJSON (jsonObject) {
    return (jsonObject && new FulfillmentStatus(jsonObject)) || undefined
  }
}

export class ItemRewardType extends EnumType {
  static ALL = 'all'
  static CASH_EQUIV = 'cash equivalent'
  static DONATION = 'donation'
  static GIFT_CARD = 'gift card'
  static OTHER = 'other'
  static PAYMENT_ACH = 'payment-ach'
  static PAYMENT_DEBIT_CARD = 'payment-card'
  static PHYSICAL_CARD = 'physical gift card'
  static REPORTING = 'reporting'

  static REWARD_LINK = 'reward link'
  static PROMO = 'promo'

  #rewardTypeID

  #notAllowed = [
    ItemRewardType.REWARD_LINK,
    ItemRewardType.PROMO
  ]

  constructor (jsonObject) {
    super(jsonObject.description, [
      ItemRewardType.ALL,
      ItemRewardType.CASH_EQUIV,
      ItemRewardType.DONATION,
      ItemRewardType.GIFT_CARD,
      ItemRewardType.OTHER,
      ItemRewardType.PAYMENT_ACH,
      ItemRewardType.PAYMENT_DEBIT_CARD,
      ItemRewardType.PHYSICAL_CARD,
      ItemRewardType.REPORTING
    ])
    if (this.#notAllowed.includes(jsonObject.description)) {
      // jsawyer - what should we do in this case?
      // throw Error(`Catalog item type ${jsonObject.description} not allowed`)
    }
    this.#rewardTypeID = jsonObject.rewardTypeID // TODO jsawyer - this is not ideal
    Object.freeze(this)
  }

  /**
   * @return {Object}
   */
  toJSON () {
    return {
      rewardTypeID: this.#rewardTypeID,
      description: this.value
    }
  }

  /**
   * @param {Object} jsonObject
   * @return {ItemRewardType|undefined}
   */
  static fromJSON (jsonObject) {
    return (jsonObject && new ItemRewardType(jsonObject)) || undefined
  }
}

export class ItemDeliveryStatus extends EnumType {
  static PROCESSED = 'PROCESSED'
  static DROPPED = 'DROPPED'
  static DELIVERED = 'DELIVERED'
  static DEFERRED = 'DEFERRED'
  static BOUNCE = 'BOUNCE'
  static BLOCKED = 'BLOCKED'
  static OPEN = 'OPEN'
  static CLICK = 'CLICK'
  static SPAMREPORT = 'SPAMREPORT'

  constructor (jsonObject) {
    super(jsonObject, [
      ItemDeliveryStatus.PROCESSED,
      ItemDeliveryStatus.DROPPED,
      ItemDeliveryStatus.DELIVERED,
      ItemDeliveryStatus.DEFERRED,
      ItemDeliveryStatus.BOUNCE,
      ItemDeliveryStatus.BLOCKED,
      ItemDeliveryStatus.OPEN,
      ItemDeliveryStatus.CLICK,
      ItemDeliveryStatus.SPAMREPORT
    ])
    Object.freeze(this)
  }

  /**
   * @param {Object} jsonObject
   * @return {ItemRewardValueType|undefined}
   */
  static fromJSON (jsonObject) {
    return (jsonObject && new ItemRewardValueType(jsonObject)) || undefined
  }
}

export class ItemRewardValueType extends EnumType {
  static VARIABLE_VALUE = 'VARIABLE_VALUE'
  static FIXED_VALUE = 'FIXED_VALUE'

  constructor (jsonObject) {
    super(jsonObject, [
      ItemRewardValueType.VARIABLE_VALUE,
      ItemRewardValueType.FIXED_VALUE
    ])
    Object.freeze(this)
  }

  /**
   * @param {Object} jsonObject
   * @return {ItemRewardValueType|undefined}
   */
  static fromJSON (jsonObject) {
    return (jsonObject && new ItemRewardValueType(jsonObject)) || undefined
  }
}

export class AdjustmentType extends EnumType {
  static FLAT = 'FLAT'
  static PERCENTAGE = 'PERCENTAGE'

  constructor (jsonObject) {
    super(jsonObject, [
      AdjustmentType.FLAT,
      AdjustmentType.PERCENTAGE
    ])
    Object.freeze(this)
  }

  /**
   * @param {Object} jsonObject
   * @return {AdjustmentType|undefined}
   */
  static fromJSON (jsonObject) {
    return (jsonObject && new AdjustmentType(jsonObject)) || undefined
  }
}

export class AdCampaignStatus extends EnumType {
  static INACTIVE = 'INACTIVE'
  static ACTIVE = 'ACTIVE'

  constructor (jsonObject) {
    super(jsonObject, [
      AdCampaignStatus.INACTIVE,
      AdCampaignStatus.ACTIVE
    ])
    Object.freeze(this)
  }

  /**
   * @param {Object} jsonObject
   * @return {AdCampaignStatus|undefined}
   */
  static fromJSON (jsonObject) {
    return (jsonObject && new AdCampaignStatus(jsonObject)) || undefined
  }
}

export class AdCampaignImageType extends EnumType {
  static DESKTOP = 'DESKTOP'
  static MOBILE = 'MOBILE'

  constructor (jsonObject) {
    super(jsonObject, [
      AdCampaignImageType.DESKTOP,
      AdCampaignImageType.MOBILE
    ])
    Object.freeze(this)
  }

  /**
   * @param {Object} jsonObject
   * @return {AdCampaignImageType|undefined}
   */
  static fromJSON (jsonObject) {
    return (jsonObject && new AdCampaignImageType(jsonObject)) || undefined
  }
}

export class RecipientEmailType extends EnumType {
  static PRIMARY = 'PRIMARY'
  static REWARDS = 'REWARDS'
  static OTHER = 'OTHER'

  constructor (jsonObject) {
    super(jsonObject, [
      RecipientEmailType.PRIMARY,
      RecipientEmailType.REWARDS,
      RecipientEmailType.OTHER
    ])
    Object.freeze(this)
  }

  /**
   * @param {Object} jsonObject
   * @return {RecipientEmailType|undefined}
   */
  static fromJSON (jsonObject) {
    return (jsonObject && new RecipientEmailType(jsonObject)) || undefined
  }
}

export class DirectPaymentAccountType extends EnumType {
  static ACH = 'AC'
  static CARD = 'CA'

  constructor (jsonObject) {
    super(jsonObject, [
      DirectPaymentAccountType.ACH,
      DirectPaymentAccountType.CARD
    ])
    Object.freeze(this)
  }

  /**
   * @param {Object} jsonObject
   * @return {DirectPaymentAccountType|undefined}
   */
  static fromJSON (jsonObject) {
    return (jsonObject && new DirectPaymentAccountType(jsonObject)) || undefined
  }
}

export class DirectPaymentStatus extends EnumType {
  static PROCESSING = 'PROCESSING'
  static OFAC_PENDING = 'OFAC_PENDING'
  static OFAC_FAILURE = 'OFAC_FAILURE'
  static COMPLETE = 'COMPLETE'
  static RETURNED = 'RETURNED'
  static FAILED = 'FAILED'

  constructor (jsonObject) {
    super(jsonObject, [
      DirectPaymentStatus.PROCESSING,
      DirectPaymentStatus.OFAC_PENDING,
      DirectPaymentStatus.OFAC_FAILURE,
      DirectPaymentStatus.COMPLETE,
      DirectPaymentStatus.RETURNED,
      DirectPaymentStatus.FAILED
    ])
    Object.freeze(this)
  }

  /**
   * @param {Object} jsonObject
   * @return {DirectPaymentStatus|undefined}
   */
  static fromJSON (jsonObject) {
    return (jsonObject && new DirectPaymentStatus(jsonObject)) || undefined
  }
}
